import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useEffect, useRef, useState } from 'react';

const INVOICE_WIDTH = 800;

export const InvoiceTemplate = ({ order, invoiceNumber }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [dims, setDims] = useState({ scale: 1, height: 0 });

  useEffect(() => {
    const calculate = () => {
      if (!containerRef.current || !contentRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const naturalHeight = contentRef.current.offsetHeight;
      const scale = Math.min(containerWidth / INVOICE_WIDTH, 1);
      setDims({ scale, height: naturalHeight * scale });
    };

    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, [order]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: dims.height || 'auto', overflow: 'hidden' }}>
      <div
        ref={contentRef}
        style={{
          width: INVOICE_WIDTH,
          transform: `scale(${dims.scale})`,
          transformOrigin: 'top left',
        }}
      >
        <div id="invoice-template" style={{ padding: '20px', background: '#fff', fontFamily: 'Arial', width: INVOICE_WIDTH, boxSizing: 'border-box' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>AK Crackers</h2>
            <p style={{ margin: '5px 0', color: '#666', fontSize: '12px' }}>Premium Fireworks & Crackers</p>
          </div>

          {/* Invoice Meta */}
          <div style={{ borderBottom: '2px solid #ddd', paddingBottom: '15px', marginBottom: '15px' }}>
            <table style={{ width: '100%', fontSize: '12px' }}>
              <tbody>
                <tr>
                  <td><strong>Invoice #:</strong> {invoiceNumber}</td>
                  <td style={{ textAlign: 'right' }}><strong>Date:</strong> {new Date(order.orderDate).toLocaleDateString()}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Details */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Customer Details</h4>
              <p style={{ margin: '3px 0', fontSize: '12px' }}><strong>{order.customerName}</strong></p>
              <p style={{ margin: '3px 0', fontSize: '12px' }}>{order.buildingNo}, {order.street}</p>
              <p style={{ margin: '3px 0', fontSize: '12px' }}>{order.area}, {order.city} - {order.pinCode}</p>
              <p style={{ margin: '3px 0', fontSize: '12px' }}>Phone: {order.phone}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Business Details</h4>
              <p style={{ margin: '3px 0', fontSize: '12px' }}>AK Crackers</p>
              <p style={{ margin: '3px 0', fontSize: '12px' }}>Sivakasi, Tamil Nadu</p>
              <p style={{ margin: '3px 0', fontSize: '12px' }}>+91 99525 65684</p>
            </div>
          </div>

          {/* Products Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Product</th>
                <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Qty</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Price</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={index}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{item.name}</td>
                  <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #eee' }}>{item.quantity}</td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>₹{item.offerPrice}</td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>₹{item.subtotal}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ textAlign: 'right', fontSize: '16px' }}>
            <strong>Grand Total: ₹{order.totalAmount}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export const generateInvoicePDF = async (order, invoiceNumber) => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '800px';
  document.body.appendChild(container);

  container.innerHTML = getInvoiceHTML(order, invoiceNumber);

  try {
    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = 210;
    const pdfHeight = 297;
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // First Page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    // Subsequent Pages
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`Invoice_${invoiceNumber}.pdf`);
  } catch (err) {
    console.error("PDF generation failed:", err);
  } finally {
    document.body.removeChild(container);
  }
};

const getInvoiceHTML = (order, invoiceNumber) => {
  const itemsHTML = order.items
    .map(
      (item) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee;color:#000;">${item.name}</td>
        <td style="padding:8px;text-align:center;border-bottom:1px solid #eee;color:#000;">${item.quantity}</td>
        <td style="padding:8px;text-align:right;border-bottom:1px solid #eee;color:#000;">₹${item.offerPrice || 0}</td>
        <td style="padding:8px;text-align:right;border-bottom:1px solid #eee;color:#000;">₹${item.subtotal || 0}</td>
      </tr>`
    )
    .join('');

  return `
    <div style="padding:20px;background:#fff;font-family:Arial;width:800px;box-sizing:border-box;color:#000;">
      <div style="text-align:center;margin-bottom:20px;">
        <h2 style="margin:0;color:#000;">AK Crackers</h2>
        <p style="margin:5px 0;color:#666;font-size:12px;">Premium Fireworks &amp; Crackers</p>
      </div>

      <div style="border-bottom:2px solid #ddd;padding-bottom:15px;margin-bottom:15px;">
        <table style="width:100%;font-size:12px;color:#000;">
          <tr>
            <td><strong>Invoice #:</strong> ${invoiceNumber}</td>
            <td style="text-align:right;"><strong>Date:</strong> ${new Date(order.orderDate).toLocaleDateString()}</td>
          </tr>
        </table>
      </div>

      <div style="display:flex;justify-content:space-between;gap:20px;margin-bottom:20px;">
        <div>
          <h4 style="margin:0 0 10px 0;font-size:14px;color:#000;">Customer Details</h4>
          <p style="margin:3px 0;font-size:12px;color:#000;"><strong>${order.customerName}</strong></p>
          <p style="margin:3px 0;font-size:12px;color:#000;">${order.buildingNo || ''}, ${order.street || ''}</p>
          <p style="margin:3px 0;font-size:12px;color:#000;">${order.city || ''} - ${order.pinCode || ''}</p>
          <p style="margin:3px 0;font-size:12px;color:#000;">Phone: ${order.phone || ''}</p>
        </div>
        <div style="text-align:right;">
          <h4 style="margin:0 0 10px 0;font-size:14px;color:#000;">Business Details</h4>
          <p style="margin:3px 0;font-size:12px;color:#000;">AK Crackers</p>
          <p style="margin:3px 0;font-size:12px;color:#000;">Sivakasi, Parapatti</p>
          <p style="margin:3px 0;font-size:12px;color:#000;">+91 99525 65684</p>
        </div>
      </div>

      <table style="width:100%;border-collapse:collapse;margin-bottom:15px;font-size:12px;color:#000;">
        <thead>
          <tr style="background:#f0f0f0;">
            <th style="padding:8px;text-align:left;border-bottom:1px solid #ddd;color:#000;">Product</th>
            <th style="padding:8px;text-align:center;border-bottom:1px solid #ddd;color:#000;">Qty</th>
            <th style="padding:8px;text-align:right;border-bottom:1px solid #ddd;color:#000;">Price</th>
            <th style="padding:8px;text-align:right;border-bottom:1px solid #ddd;color:#000;">Total</th>
          </tr>
        </thead>
        <tbody>${itemsHTML}</tbody>
      </table>

      <div style="display:flex;justify-content:flex-end;margin-bottom:20px;">
        <div style="width:250px;">
          <div style="display:flex;justify-content:space-between;padding:8px 0;border-top:2px solid #000;color:#000;">
            <strong>Grand Total:</strong>
            <strong style="font-size:16px;">₹${order.totalAmount}</strong>
          </div>
        </div>
      </div>
    </div>
  `;
};